// GPU-side timing via EXT_disjoint_timer_query_webgl2.
//
// performance.now() around WebGL calls only measures CPU command-submission;
// the actual GPU work is queued and runs asynchronously. To attribute time
// to vertex/fragment shaders we need timer queries. Results land 1-3 frames
// late, so each scope keeps a ring of in-flight queries and we read the
// oldest available one. If the extension is missing (some headless setups,
// older drivers) the timer becomes a no-op reporting 0 ms.

const RING_SIZE = 4;

type Ext = {
  TIME_ELAPSED_EXT: number;
  GPU_DISJOINT_EXT: number;
};

type Scope = {
  queries: (WebGLQuery | null)[];
  inFlight: boolean[];
  writeIndex: number;
  lastResultMs: number;
};

export class GpuTimer {
  private gl: WebGL2RenderingContext;
  private ext: Ext | null;
  private scopes = new Map<string, Scope>();
  private activeScope: string | null = null;

  constructor(gl: WebGL2RenderingContext) {
    this.gl = gl;
    const ext = gl.getExtension('EXT_disjoint_timer_query_webgl2') as Ext | null;
    this.ext = ext;
    if (!ext) {
      console.info('[GpuTimer] EXT_disjoint_timer_query_webgl2 unavailable; GPU times will be 0.');
    }
  }

  public get available(): boolean {
    return this.ext !== null;
  }

  // Call once at the top of the frame, before any begin(). When the GPU
  // timeline crosses a disjoint event (mode switch, etc.) all in-flight
  // measurements are unreliable and must be discarded per the spec.
  public beginFrame(): void {
    if (!this.ext) return;
    const disjoint = this.gl.getParameter(this.ext.GPU_DISJOINT_EXT) as boolean;
    if (disjoint) {
      for (const scope of this.scopes.values()) {
        for (let i = 0; i < RING_SIZE; i++) scope.inFlight[i] = false;
      }
    }
  }

  public begin(name: string): void {
    if (!this.ext) return;
    if (this.activeScope !== null) {
      throw new Error(
        `[GpuTimer] cannot begin '${name}' while '${this.activeScope}' is active (TIME_ELAPSED queries cannot nest)`,
      );
    }
    const scope = this.getOrCreateScope(name);
    const slot = scope.writeIndex;
    let q = scope.queries[slot];
    if (!q) {
      q = this.gl.createQuery();
      scope.queries[slot] = q;
    }
    if (scope.inFlight[slot]) {
      // Slot still busy from a prior frame — drop oldest measurement to
      // recycle. With RING_SIZE=4 this only happens if results stall (rare).
      scope.inFlight[slot] = false;
    }
    this.gl.beginQuery(this.ext.TIME_ELAPSED_EXT, q!);
    scope.inFlight[slot] = true;
    this.activeScope = name;
  }

  public end(): void {
    if (!this.ext || this.activeScope === null) return;
    const scope = this.scopes.get(this.activeScope)!;
    this.gl.endQuery(this.ext.TIME_ELAPSED_EXT);
    scope.writeIndex = (scope.writeIndex + 1) % RING_SIZE;
    this.activeScope = null;
  }

  // Polls the ring for any settled result and updates lastResultMs. Call
  // once per frame per scope (or just call getLastResultMs which polls).
  public poll(name: string): number {
    const scope = this.scopes.get(name);
    if (!scope || !this.ext) return 0;
    const gl = this.gl;
    // Walk ring oldest-first relative to writeIndex; settle whatever is
    // ready and keep the most recent settled value.
    for (let i = 0; i < RING_SIZE; i++) {
      const slot = (scope.writeIndex + i) % RING_SIZE;
      if (!scope.inFlight[slot]) continue;
      const q = scope.queries[slot]!;
      const ready = gl.getQueryParameter(q, gl.QUERY_RESULT_AVAILABLE) as boolean;
      if (!ready) continue;
      const ns = gl.getQueryParameter(q, gl.QUERY_RESULT) as number;
      scope.lastResultMs = ns / 1_000_000;
      scope.inFlight[slot] = false;
    }
    return scope.lastResultMs;
  }

  public getLastResultMs(name: string): number {
    return this.poll(name);
  }

  public destroy(): void {
    for (const scope of this.scopes.values()) {
      for (const q of scope.queries) {
        if (q) this.gl.deleteQuery(q);
      }
    }
    this.scopes.clear();
    this.activeScope = null;
  }

  private getOrCreateScope(name: string): Scope {
    let scope = this.scopes.get(name);
    if (!scope) {
      scope = {
        queries: new Array(RING_SIZE).fill(null),
        inFlight: new Array(RING_SIZE).fill(false),
        writeIndex: 0,
        lastResultMs: 0,
      };
      this.scopes.set(name, scope);
    }
    return scope;
  }
}
